import React, { useEffect, useRef, useState } from 'react'
import {
    Card,
    Flex,
    Input,
    type MenuProps,
    Radio,
    type FormInstance,
    type InputRef,
    Form,
    Space,
    Popover,
    Switch,
} from 'antd'
import Dropdown from 'antd/es/dropdown/dropdown'
import { ReportStatusDeleteStatusModal } from './ReportStatusDeleteModal'
import { ConfirmModal } from '../modals/confirmModal'
import {
    isTeam,
    isUser,
    type Assignable,
    type AssigneeIds,
    type Team,
    type User,
} from '../../../shared/domain/schedule/assignee.no-deps'
import type { ReportStatus } from './ReportStatusFlowSettings'
import { AdvancedSettings } from './AdvancedSettings'
import type { UUID } from '../../../shared/lib/typeHelpers.no-deps'
import type { Flags } from '../../../shared/cfg/flags.no-deps'
import { IconButton } from '../ui/IconButton'
import { AssignableIcon } from '../../../shared/webcomponents/react-wrappers/AssignableIcon'
import { firstBy, last, map, pipe, identity } from 'remeda'
import { SearchableItemList } from '../ui/SearchableItemList'
import AssigneeSelect from '../../../shared/webcomponents/react-wrappers/AssigneeSelect'
import { Translations } from '../../../shared/i18n/Translations'

export type ReportStatusRow = ReportStatus & {
    statusIndex: number
    isDefaultStatus: boolean
    isFinalStatus: boolean
    canBeChangedToStatuses: UUID[]
    canBeChangedByAssignees: AssigneeIds
    notifyUsersWhenStatusBecomesActive: boolean
    isAdvancedSettingsOpen: boolean
}

export type AdvancedSettingsForm = Pick<
    ReportStatusRow,
    'isFinalStatus' | 'canBeChangedByAssignees' | 'canBeChangedToStatuses' | 'notifyUsersWhenStatusBecomesActive'
> & {
    isDirty: boolean
}

interface Props {
    setAnyAdvancedSettingsDirty: (isEditorDirty: boolean) => void
    row: ReportStatusRow
    allStatuses: ReportStatus[]
    allUsers: User[]
    allTeams: Team[]
    onNameInputChange: (name: string) => void
    onCanBeChangedToChanged: (statuses: UUID[]) => void
    onNotifyUsersWhenStatusBecomesActiveChanged: (notifyUsersWhenStatusBecomesActive: boolean) => void
    onCanBeChangedByChanged: (assigneeIds: AssigneeIds) => void
    onFinalStatusChanged: () => void
    onNameValidationErrorChange: (errors: string[]) => void
    // deprecated The advanced settings component is going completely we can remove this also when that goes.
    onSave: (settings: AdvancedSettingsForm) => void
    onDeleteActionClick: (index: number, options?: { remapToStatusIndex: number | null }) => void
    autoFocusInput?: boolean
    // deprecated The advanced settings component is going completely we can remove this also when that goes.
    openAdvancedSettings: () => void
    // deprecated The advanced settings component is going completely we can remove this also when that goes.
    closeAdvancedSettings: () => void
    // deprecated The advanced settings component is going completely we can remove this also when that goes.
    isAnyAdvancedSettingsDirty: boolean
    isDisabled: boolean
    // deprecated - can remove once approvalFlowsMultipleFinals flag is on for all customers
    isAnyOtherStatusFinal: boolean
    showValidationErrors: boolean
    featureFlags: Partial<Flags>
    errorMessage: string
}

export function ReportStatusRow(props: Props) {
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
    const [shouldShowErrorMessage, setShouldShowErrorMessage] = useState(false)
    const inputRef = useRef<InputRef>(null)
    const [formInstance] = useState<FormInstance>(Form.useForm().pop())

    useEffect(() => {
        setIsCancelModalOpen(false)
    }, [props.row.isAdvancedSettingsOpen])

    useEffect(() => {
        getNameValidatonErrors(formInstance).then((errors) => {
            props.onNameValidationErrorChange(errors)
            if (!errors.length) {
                setShouldShowErrorMessage(false)
            }
        })
    }, [props.allStatuses])

    const onCancel = (isDirty: boolean) => {
        if (isDirty) {
            setIsCancelModalOpen(true)
        } else {
            props.closeAdvancedSettings()
        }
    }

    const onSave = (settings: AdvancedSettingsForm) => {
        props.closeAdvancedSettings()
        props.onSave(settings)
    }

    const onDeleteClick = () => {
        if (props.row.id) {
            setIsDeleteModalOpen(true)
        } else {
            // if there is no id this status has never been saved and therefore never used, no need to transfer this status
            // to any other, we just delete it
            props.onDeleteActionClick(props.row.statusIndex)
        }
    }

    const onDeleteConfirmClick = (remapToStatusIndex: number) => {
        props.onDeleteActionClick(props.row.statusIndex, { remapToStatusIndex })
        setIsDeleteModalOpen(false)
    }

    const onNameEntered = () => {
        formInstance.setFieldValue(
            'statusName',
            formInstance.getFieldValue('statusName').trim() ||
                `${window.i18n('appcore:reportStatus.emptyNamePlaceholder')} ${getNextNamePlaceholderIndex(
                    props.allStatuses
                )}`
        )
        props.onNameInputChange(formInstance.getFieldValue('statusName'))
        getNameValidatonErrors(formInstance).then(() => {
            setShouldShowErrorMessage(true)
        })
    }

    const assignees = [
        ...props.row.canBeChangedByAssignees.users.map((id) => props.allUsers.find(({ userId }) => userId === id)),
        ...props.row.canBeChangedByAssignees.teams.map((teamId) => props.allTeams.find(({ id }) => teamId === id)),
    ]

    return (
        <>
            {!props.row.isAdvancedSettingsOpen && (
                <>
                    <Card className={'report-status__row_container'}>
                        <Flex className="report-status__row" data-testid="report-status-row" id={props.row.uuid}>
                            <Flex className="report-status__row_name">
                                <Form layout="vertical" form={formInstance}>
                                    <Form.Item
                                        style={{ margin: 0 }}
                                        name="statusName"
                                        initialValue={props.row.name}
                                        rules={[
                                            {
                                                validator: (_, value) => {
                                                    if (
                                                        isStatusNameADuplicate(props.allStatuses, props.row.uuid, value)
                                                    ) {
                                                        return Promise.reject(
                                                            new Error(
                                                                window.i18n(
                                                                    'appcore:ValidationMessage.statusNameMustBeUnique'
                                                                )
                                                            )
                                                        )
                                                    } else {
                                                        return Promise.resolve()
                                                    }
                                                },
                                            },
                                        ]}
                                        help="" // so it's not displayed but the error string[] has the translated error message
                                        validateTrigger={[]}
                                    >
                                        <Input.TextArea
                                            autoSize={{ minRows: 1, maxRows: 2 }}
                                            disabled={props.isDisabled}
                                            autoComplete="off"
                                            variant="borderless"
                                            ref={inputRef}
                                            autoFocus={!props.row.name}
                                            onBlur={onNameEntered}
                                            onPressEnter={() => inputRef.current?.blur()}
                                            maxLength={50}
                                            data-testid="report-status-name-input"
                                        />
                                    </Form.Item>
                                </Form>
                                {/* deprecated - can remove this once the flag is always on */}
                                {!props.featureFlags['approval-flows-multiple-finals'] && props.row.isFinalStatus && (
                                    <span className="report-status__row--info-label">
                                        {'(' + window.i18n('appcore:reportStatus.final') + ')'}
                                    </span>
                                )}
                            </Flex>
                            <div style={{ flex: 1 }}>
                                <Radio
                                    disabled={props.isDisabled}
                                    value={props.row.uuid}
                                    data-testid={'report-status-default-radio'}
                                />
                            </div>
                            <CanBeChangedTo
                                canBeChangedToStatuses={props.row.canBeChangedToStatuses}
                                allStatuses={props.allStatuses}
                                featureFlags={props.featureFlags}
                                row={props.row}
                                onCanBeChangedToChanged={props.onCanBeChangedToChanged}
                            />
                            <CanBeChangedBy
                                assignees={assignees}
                                featureFlags={props.featureFlags}
                                allUsers={props.allUsers}
                                allTeams={props.allTeams}
                                onCanBeChangedByChanged={props.onCanBeChangedByChanged}
                            />
                            <Notifications
                                isDisabled={assignees.length === 0}
                                notifyUsersWhenStatusBecomesActive={props.row.notifyUsersWhenStatusBecomesActive}
                                featureFlags={props.featureFlags}
                                onNotifyUsersWhenStatusBecomesActiveChanged={
                                    props.onNotifyUsersWhenStatusBecomesActiveChanged
                                }
                            />
                            {props.featureFlags['approval-flows-multiple-finals'] && (
                                <div>
                                    <Switch
                                        data-testid="report-status-final-switch"
                                        value={props.row.isFinalStatus}
                                        onChange={() => props.onFinalStatusChanged()}
                                    />
                                </div>
                            )}
                            <Flex className="report-status__row_menu">
                                {/* deprecated - can remove this once the flag is always on */}
                                {!props.featureFlags['approval-flows-multiple-finals'] && (
                                    <IconButton
                                        size="small"
                                        border={false}
                                        onClick={() => props.openAdvancedSettings()}
                                        disabled={props.isAnyAdvancedSettingsDirty}
                                        data-testid="report-status-row-advanced-settings"
                                        iconName="advanced-settings"
                                        tooltip={window.i18n('appcore:reportStatus.advancedSettingsTitle')}
                                    />
                                )}
                                <Dropdown
                                    disabled={props.isAnyAdvancedSettingsDirty}
                                    menu={{
                                        items: getMenuItems({ isDefaultStatus: props.row.isDefaultStatus }),
                                        onClick: contextMenuClickHandler(onDeleteClick),
                                    }}
                                    trigger={['click']}
                                >
                                    <IconButton
                                        size="small"
                                        border={false}
                                        data-testid="report-status-context-menu"
                                        iconName="ellipsis"
                                    />
                                </Dropdown>
                            </Flex>
                        </Flex>
                    </Card>
                    {shouldShowErrorMessage && props.errorMessage && (
                        <Space
                            size="small"
                            className="has-text-danger has-font-size-small"
                            style={{
                                marginLeft: '1.6rem',
                                marginTop: '0.8rem',
                            }}
                        >
                            <zaptic-icon icon-name="red-warning-icon"></zaptic-icon>
                            {props.errorMessage}
                        </Space>
                    )}
                </>
            )}
            <ReportStatusDeleteStatusModal
                status={props.row}
                statusIndex={props.row.statusIndex}
                allStatuses={props.allStatuses}
                isModalOpen={isDeleteModalOpen}
                onCancelClicked={() => setIsDeleteModalOpen(false)}
                onDeleteConfirmClicked={(remapToStatusId) => onDeleteConfirmClick(remapToStatusId)}
            />
            <AdvancedSettings
                row={props.row}
                allUsers={props.allUsers}
                allTeams={props.allTeams}
                allStatuses={props.allStatuses}
                isOpen={props.row.isAdvancedSettingsOpen}
                onCancelClicked={(isDirty) => onCancel(isDirty)}
                onSaveClicked={(settings) => onSave(settings)}
                isAnyOtherStatusFinal={props.isAnyOtherStatusFinal}
                showValidationErrors={props.showValidationErrors}
                openAdvancedSettings={props.openAdvancedSettings}
                featureFlags={props.featureFlags}
                setAnyAdvancedSettingsDirty={props.setAnyAdvancedSettingsDirty}
            />
            <ConfirmModal
                title={window.i18n('appcore:reportStatus.discardChangesTitle')}
                confirmButtonText={window.i18n('appcore:reportStatus.discardChanges')}
                open={isCancelModalOpen}
                onCancel={() => setIsCancelModalOpen(false)}
                onConfirmClicked={() => props.closeAdvancedSettings()}
            >
                <p>
                    {window.i18n('appcore:reportStatus.discardChangesDescription')}
                    <span className="CancelAdvancedSettingsModal__message"> {props.row.name}</span>?
                </p>
            </ConfirmModal>
        </>
    )
}

async function getNameValidatonErrors(formInstance: FormInstance<any>): Promise<string[]> {
    return formInstance.getFieldValue('statusName')
        ? formInstance
              .validateFields()
              .catch(() => {
                  /*make sure validation happens before getFieldError() */
              })
              .then(() => formInstance.getFieldError('statusName'))
        : []
}

function getNextNamePlaceholderIndex(allStatuses: ReportStatus[]) {
    return (
        pipe(
            allStatuses,
            map(getIndexAfterPlaceholder),
            map(last),
            map((a) => Number.parseInt((a as unknown as string) ?? '0', 10)),
            firstBy([identity, 'desc'])
        ) + 1
    )
}

function getIndexAfterPlaceholder(status: ReportStatus) {
    return (
        new RegExp(`^${window.i18n('appcore:reportStatus.emptyNamePlaceholder')}\\s+(?<index>\\d+)$`, 'i').exec(
            status.name
        )?.groups.index ?? 0
    )
}

function isStatusNameADuplicate(allStatuses: ReportStatus[], currentUUID: UUID, value: string) {
    return allStatuses.some(
        (status) => status.name.toLocaleLowerCase() === value.toLocaleLowerCase() && status.uuid !== currentUUID
    )
}

function contextMenuClickHandler(onDeleteClick: () => void): MenuProps['onClick'] {
    return (e) => {
        if (e.key === 'delete') {
            onDeleteClick()
        }
    }
}

function CanBeChangedTo(props: {
    canBeChangedToStatuses: UUID[]
    allStatuses: ReportStatus[]
    featureFlags: Partial<Flags>
    row: ReportStatusRow
    onCanBeChangedToChanged: (statuses: UUID[]) => void
}) {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false)

    // deprecated, can remove this once the feature flag goes
    if (!props.featureFlags['approval-flows-multiple-finals']) {
        return (
            <div className="display-on-two-rows" data-testid="can-be-changed-to">
                {props.canBeChangedToStatuses
                    .map((selectedUUID) => props.allStatuses.find(({ uuid }) => uuid === selectedUUID)?.name ?? '?')
                    .join(', ') || window.i18n('appcore:reportStatus.anyStatus')}
            </div>
        )
    }

    return (
        <Popover
            trigger="click"
            content={
                <CanBeChangedToSelect
                    allStatuses={props.allStatuses}
                    row={props.row}
                    onCanBeChangedToChanged={props.onCanBeChangedToChanged}
                />
            }
            open={isPopoverOpen}
            onOpenChange={setIsPopoverOpen}
            arrow={false}
            placement="bottomLeft"
            overlayClassName="report-status__row-can-be-changed-to__popover"
            overlayStyle={{ animationDuration: '0s' }}
        >
            <div
                className="report-status__row-can-be-changed-to__container display-on-two-rows"
                data-testid="can-be-changed-to"
                style={{ flex: 2 }}
            >
                {props.canBeChangedToStatuses
                    .map((selectedUUID) => props.allStatuses.find(({ uuid }) => uuid === selectedUUID)?.name ?? '?')
                    .join(', ') || window.i18n('appcore:reportStatus.anyStatus')}
                <Chevron />
            </div>
        </Popover>
    )
}

function Notifications(props: {
    isDisabled: boolean
    notifyUsersWhenStatusBecomesActive: boolean
    featureFlags: Partial<Flags>
    onNotifyUsersWhenStatusBecomesActiveChanged: (notifyUsersWhenStatusBecomesActive: boolean) => void
}) {
    if (props.featureFlags['approval-flows-notifications']) {
        return (
            <Flex className="report-status__row-notify-users" data-testid="report-status__row-notify-users" flex={2}>
                {
                    <Switch
                        data-testid="report-status-notifications-switch"
                        value={props.notifyUsersWhenStatusBecomesActive}
                        onChange={() =>
                            props.onNotifyUsersWhenStatusBecomesActiveChanged(!props.notifyUsersWhenStatusBecomesActive)
                        }
                        disabled={props.isDisabled}
                        title={
                            props.isDisabled
                                ? window.i18n('appcore:reportStatus.notificationsToggleDisabledText')
                                : undefined
                        }
                    />
                }
            </Flex>
        )
    }
}

function Chevron() {
    return (
        <svg
            className="chevron"
            width="12"
            height="7"
            viewBox="0 0 12 7"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.7365 0.225291C12.0458 0.530861 12.0458 1.03024 11.7365 1.33581L6.90974 6.10455C6.39859 6.60955 5.56987 6.60955 5.05873 6.10455L0.231966 1.33581C-0.0773221 1.03024 -0.077322 0.530861 0.231966 0.225291C0.536009 -0.0750969 1.0251 -0.0750972 1.32914 0.225291L5.98423 4.82442L10.6393 0.225291C10.9434 -0.075097 11.4325 -0.0750967 11.7365 0.225291Z"
                fill="#7E8B9E"
            />
        </svg>
    )
}

function CanBeChangedToSelect(props: {
    allStatuses: ReportStatus[]
    row: ReportStatusRow
    onCanBeChangedToChanged: (statuses: UUID[]) => void
}) {
    const onItemsSelectedHandler = (items: string[]) => {
        props.onCanBeChangedToChanged(items.map((item) => item as UUID))
    }

    return (
        <>
            <div className="status-container">
                <SearchableItemList
                    onItemsSelected={onItemsSelectedHandler}
                    selectedItems={props.row.canBeChangedToStatuses.map((uuid) => uuid.toString())}
                    items={props.allStatuses
                        .filter((status) => status.uuid !== props.row.uuid)
                        .map((status) => ({ id: status.uuid, name: status.name }))}
                    helpText={window.i18n('appcore:reportStatus.anyStatusHelpText')}
                />
            </div>
        </>
    )
}

function CanBeChangedBy(props: {
    assignees: Assignable[]
    featureFlags: Partial<Flags>
    allUsers: User[]
    allTeams: Team[]
    onCanBeChangedByChanged: (assigneeIds: AssigneeIds) => void
}) {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false)

    const areThereAssignees = !!props.assignees.length
    const AdditionalAssigneesBesidesFirstTwo = () =>
        props.assignees.length > 2 ? <span>,&nbsp;+{props.assignees.length - 2}</span> : null
    const assigneesDisplayed = props.assignees.slice(0, 2)

    // deprecated - can remove this once the feature flag goes
    if (!props.featureFlags['approval-flows-multiple-finals']) {
        return (
            <Flex className="can-be-changed-by" data-testid="can-be-changed-by" flex={2}>
                {areThereAssignees ? (
                    <>
                        {assigneesDisplayed.map((assignee, i) => (
                            <Flex key={i} align="center" className="report-status__row-can-be-changed-by__assignee">
                                <AssignableIcon
                                    assignee={assignee}
                                    style={
                                        {
                                            marginRight: '0.1rem',
                                            lineHeight: '100%',
                                            '--TeamCircle-size-override': '20px',
                                        } as React.CSSProperties
                                    }
                                />
                                {assignee.name}
                            </Flex>
                        ))}
                        <AdditionalAssigneesBesidesFirstTwo />
                    </>
                ) : (
                    window.i18n('appcore:reportStatus.anyone')
                )}
            </Flex>
        )
    }

    return (
        <Popover
            trigger="click"
            content={
                <CanBeChangedBySelect
                    assignees={props.assignees}
                    allUsers={props.allUsers}
                    allTeams={props.allTeams}
                    onCanBeChangedByChanged={props.onCanBeChangedByChanged}
                />
            }
            open={isPopoverOpen}
            onOpenChange={setIsPopoverOpen}
            arrow={false}
            placement="bottomLeft"
            overlayClassName="report-status__row-can-be-changed-by__popover"
            overlayStyle={{ animationDuration: '0s' }}
        >
            <div
                className="report-status__row-can-be-changed-by__container display-on-two-rows"
                data-testid="can-be-changed-by"
                style={{ flex: 2 }}
            >
                {areThereAssignees ? (
                    <>
                        {assigneesDisplayed.map((assignee, i) => (
                            <Flex key={i} align="center" className="report-status__row-can-be-changed-by__assignee">
                                <AssignableIcon
                                    assignee={assignee}
                                    style={
                                        {
                                            marginRight: '0.2rem',
                                            lineHeight: '100%',
                                            '--TeamCircle-size-override': '20px',
                                        } as React.CSSProperties
                                    }
                                />
                                {assignee.name}
                            </Flex>
                        ))}
                        <AdditionalAssigneesBesidesFirstTwo />
                    </>
                ) : (
                    window.i18n('appcore:reportStatus.anyone')
                )}
                <Chevron />
            </div>
        </Popover>
    )
}

function CanBeChangedBySelect(props: {
    assignees: Assignable[]
    allUsers: User[]
    allTeams: Team[]
    onCanBeChangedByChanged: (assigneeIds: AssigneeIds) => void
}) {
    return (
        <div className="status-container">
            <AssigneeSelect
                placeholder={window.i18n('appcore:common.search')}
                users={props.allUsers}
                teams={props.allTeams}
                onChange={({ detail: assignedTo = [] }) => {
                    props.onCanBeChangedByChanged({
                        users: assignedTo.filter(isUser).map((assignee) => assignee.userId),
                        teams: assignedTo.filter(isTeam).map((assignee) => assignee.id),
                    })
                }}
                selectedIds={{
                    users: props.assignees.filter(isUser).map((assignee) => assignee.userId),
                    teams: props.assignees.filter(isTeam).map((assignee) => assignee.id),
                }}
                language={Translations.language}
                multi-select={true}
                allow-select-all-teams={false}
                search-dropdown={false}
                show-clear-all-button={false}
                input-search-at-top={true}
                help-text={window.i18n('appcore:reportStatus.assigneeSelectHelperText')}
            />
        </div>
    )
}

function getMenuItems(a: { isDefaultStatus: boolean }): MenuProps['items'] {
    return [
        {
            label: window.i18n('appcore:reportStatus.deleteStatus'),
            icon: <zaptic-icon icon-name="delete-trash" />,
            key: 'delete',
            disabled: a.isDefaultStatus,
            danger: true,
        },
    ]
}
